import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { membersAPI, pendingAdmissionAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { AdmitModal, InviteToPayModal } from './PendingAdmissions';

const STATUS_STYLES = {
  'PRACTISING MEMBER':     'bg-emerald-100 text-emerald-700',
  'NON-PRACTISING MEMBER': 'bg-amber-100 text-amber-700',
  'NOTARY MEMBER':         'bg-blue-100 text-blue-700',
  'DECEASED':              'bg-red-100 text-red-700',
  'PENDING_ADMISSION':     'bg-orange-100 text-orange-700',
};

const STATUS_CARD_STYLES = {
  'PRACTISING MEMBER':     'border-emerald-200 bg-emerald-50 text-emerald-700',
  'NON-PRACTISING MEMBER': 'border-amber-200 bg-amber-50 text-amber-700',
  'NOTARY MEMBER':         'border-blue-200 bg-blue-50 text-blue-700',
  'DECEASED':              'border-red-200 bg-red-50 text-red-700',
  'PENDING_ADMISSION':     'border-orange-200 bg-orange-50 text-orange-700',
};

function statusStyle(s) {
  return STATUS_STYLES[(s || '').toUpperCase()] || 'bg-slate-100 text-slate-500';
}

function statusCardStyle(s) {
  return STATUS_CARD_STYLES[(s || '').toUpperCase()] || 'border-slate-200 bg-slate-50 text-slate-600';
}

function statusShort(s) {
  const map = {
    'PRACTISING MEMBER':     'Practising',
    'NON-PRACTISING MEMBER': 'Non-Practising',
    'NOTARY MEMBER':         'Notary',
    'DECEASED':              'Deceased',
    'PENDING_ADMISSION':     'Pending Admission',
  };
  return map[(s || '').toUpperCase()] || s || 'Unknown';
}

function avatarColor(name = '') {
  const colors = ['bg-orange-500','bg-violet-600','bg-emerald-600','bg-rose-500','bg-indigo-600','bg-orange-500','bg-amber-500'];
  return colors[(name.charCodeAt(0) || 0) % colors.length];
}

export default function Members() {
  const { user } = useAuthStore();
  const [members, setMembers]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);
  const [search, setSearch]             = useState('');
  const [activeTab, setActiveTab]       = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [admissionYear, setAdmissionYear] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [exemptedMembers, setExempted]  = useState([]);
  const [exemptTotal, setExemptTotal]   = useState(0);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingPagination, setPendingPagination] = useState({
    page: 1, total: 0, total_pages: 1, has_next: false, has_previous: false,
  });
  const [admitTarget, setAdmitTarget] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [pagination, setPagination]     = useState({
    page: 1, total: 0, total_pages: 1, has_next: false, has_previous: false,
  });
  const [showInvite, setShowInvite]       = useState(false);
  const [inviteForm, setInviteForm]       = useState({ roll_no: '', email: '', phone: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError]     = useState('');
  const [inviteResult, setInviteResult]   = useState(null);
  const [stats, setStats] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    membersAPI.stats().then(setStats).catch(() => setStats(null));
  }, []);

  function loadPending() {
    setPendingLoading(true);
    pendingAdmissionAPI.list(page, 20, search, '')
      .then((res) => {
        setPendingRecords(res.pending_admissions || []);
        setPendingPagination({
          page: res.page || 1,
          total: res.total || 0,
          total_pages: res.total_pages || 1,
          has_next: !!res.has_next,
          has_previous: !!res.has_previous,
        });
      })
      .catch(console.error)
      .finally(() => setPendingLoading(false));
  }

  useEffect(() => {
    if (activeTab === 'pending') {
      loadPending();
      return;
    }

    let cancelled = false;
    setLoading(true);
    if (activeTab === 'exempted') {
      membersAPI.exempted(search)
        .then((res) => {
          if (cancelled) return;
          setExempted(res.members || []);
          setExemptTotal(res.total || 0);
        })
        .catch(console.error)
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      membersAPI.list(page, 20, search, activeTab === 'deceased' ? 'DECEASED' : statusFilter, admissionYear)
        .then((res) => {
          if (cancelled) return;
          setMembers(res.members || []);
          setPagination({
            page: res.page || 1,
            total: res.total || 0,
            total_pages: res.total_pages || 1,
            has_next: !!res.has_next,
            has_previous: !!res.has_previous,
          });
        })
        .catch(console.error)
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [page, search, activeTab, statusFilter, admissionYear]);

  function handleSearch(e) {
    const v = e.target.value;
    setSearchInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(v); setPage(1); }, 300);
  }

  function selectCard(status = '', year = '') {
    clearTimeout(searchTimer.current);
    setSearch('');
    setSearchInput('');
    setStatusFilter(status);
    setAdmissionYear(year);
    setActiveTab(status.toUpperCase() === 'DECEASED' ? 'deceased' : 'all');
    setPage(1);
  }

  function cardClass(selected) {
    return `text-left transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${selected ? 'ring-2 ring-orange-500 shadow-sm' : ''}`;
  }

  async function handleSendInvitation(e) {
    e.preventDefault();
    setInviteLoading(true); setInviteError(''); setInviteResult(null);
    try {
      const res = await membersAPI.sendInvitation(inviteForm);
      setInviteResult(res);
      setInviteForm({ roll_no: '', email: '', phone: '' });
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invitation.');
    } finally { setInviteLoading(false); }
  }

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Members</h1>
          <p className="text-sm text-slate-400 mt-0.5">{(stats?.total_members ?? pagination.total).toLocaleString()} members total</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/deceased-members"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
            Deceased Dashboard
          </Link>
          {user?.role === 'admin' && (
            <button onClick={() => { setShowInvite(v => !v); setInviteError(''); setInviteResult(null); }}
              className="rounded-xl bg-orange-600 hover:bg-orange-500 text-white px-3 py-2 text-sm font-medium transition-colors">
              {showInvite ? 'Close' : '+ Invite Member'}
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
          <button type="button" onClick={() => selectCard()}
            aria-pressed={activeTab === 'all' && !statusFilter && !admissionYear}
            className={`rounded-2xl border border-slate-200 bg-white p-4 ${cardClass(activeTab === 'all' && !statusFilter && !admissionYear)}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Members</p>
            <p className="mt-1 text-2xl font-bold text-slate-800">{(stats.total_members ?? 0).toLocaleString()}</p>
          </button>
          <button type="button" onClick={() => selectCard('', stats.admission_year)}
            aria-pressed={!!admissionYear}
            className={`rounded-2xl border border-orange-200 bg-orange-50 p-4 ${cardClass(!!admissionYear)}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-orange-500">Admitted {stats.admission_year}</p>
            <p className="mt-1 text-2xl font-bold text-orange-700">{(stats.admitted_this_year ?? 0).toLocaleString()}</p>
          </button>
          {Object.entries(stats.by_status || {}).map(([status, count]) => (
            <button type="button" key={status} onClick={() => selectCard(status)}
              aria-pressed={statusFilter === status || (activeTab === 'deceased' && status.toUpperCase() === 'DECEASED')}
              className={`rounded-2xl border p-4 ${statusCardStyle(status)} ${cardClass(statusFilter === status || (activeTab === 'deceased' && status.toUpperCase() === 'DECEASED'))}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">{statusShort(status)}</p>
              <p className="mt-1 text-2xl font-bold">{count.toLocaleString()}</p>
            </button>
          ))}
        </div>
      )}

      {/* Invite form */}
      {showInvite && user?.role === 'admin' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Send Invitation</h2>
          {inviteError   && <p className="mb-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{inviteError}</p>}
          {inviteResult  && (
            <div className="mb-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">
              <p className="font-semibold">{inviteResult.message}</p>
              {inviteResult.invitation_url && <p className="mt-1 break-all text-xs">{inviteResult.invitation_url}</p>}
            </div>
          )}
          <form onSubmit={handleSendInvitation} className="grid gap-3 sm:grid-cols-3">
            {[['Roll Number','roll_no','text'],['Email','email','email'],['Phone','phone','tel']].map(([label, name, type]) => (
              <div key={name}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                <input type={type} required value={inviteForm[name]}
                  onChange={e => setInviteForm(p => ({...p, [name]: e.target.value}))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            ))}
            <div className="sm:col-span-3">
              <button type="submit" disabled={inviteLoading}
                className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-5 py-2 text-sm font-medium">
                {inviteLoading ? 'Sending…' : 'Send Invitation'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabs + Search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 self-start">
          {[['all','All Members'],['deceased','Deceased'],['exempted','Fee Exempt'],['pending','Pending Admission']].map(([key, label]) => (
            <button key={key} onClick={() => { setActiveTab(key); setStatusFilter(''); setAdmissionYear(''); setPage(1); }}
              className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                activeTab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {label}
              {key === 'exempted' && exemptTotal > 0 && (
                <span className="ml-1.5 rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-xs font-bold">{exemptTotal}</span>
              )}
              {key === 'pending' && pendingPagination.total > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-xs font-bold">{pendingPagination.total}</span>
              )}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z"/>
          </svg>
          <input type="text" placeholder="Search name or roll no…" value={searchInput}
            onChange={handleSearch}
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
        </div>
      </div>

      {(statusFilter || admissionYear) && (
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <p>Showing: <span className="font-semibold">{admissionYear ? `Admitted ${admissionYear}` : statusShort(statusFilter)}</span></p>
          <button type="button" onClick={() => selectCard()} className="font-medium text-orange-600 hover:underline">Show all members</button>
        </div>
      )}

      {/* ── Exempted Members Tab ── */}
      {activeTab === 'exempted' && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {/* Banner */}
          <div className="border-b border-emerald-100 bg-emerald-50 px-5 py-3 flex items-center gap-3">
            <span className="text-lg">🎖</span>
            <div>
              <p className="text-sm font-bold text-emerald-800">Annual Fee Exemption List</p>
              <p className="text-xs text-emerald-600">Members aged 66 and above — exempted from annual contributions (AGM Resolution No. 5, 1st July 2026)</p>
            </div>
            <span className="ml-auto shrink-0 rounded-full bg-emerald-100 border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700">
              {exemptTotal} member{exemptTotal !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
          ) : exemptedMembers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-1">
              <p className="text-slate-500 font-medium text-sm">No exempt members found</p>
              <p className="text-xs text-slate-400">No members with date of birth recorded are aged 66+</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="w-12 px-4 py-3" />
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400 w-24">Roll No</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400 w-16">Age</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Date of Birth</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Chapter</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Phone</th>
                      <th className="px-4 py-3 w-20" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {exemptedMembers.map((m) => (
                      <tr key={m.id} className="hover:bg-emerald-50/40 transition-colors">
                        <td className="px-4 py-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${avatarColor(m.full_name)}`}>
                            {(m.full_name || '?')[0].toUpperCase()}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{m.full_name || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{m.roll_no || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-bold">{m.age}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{m.date_of_birth || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusStyle(m.status)}`}>
                            {statusShort(m.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{m.chapter || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{m.phone || '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/members/${m.id}`}
                            className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors whitespace-nowrap">
                            View →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <ul className="sm:hidden divide-y divide-slate-100">
                {exemptedMembers.map((m) => (
                  <li key={m.id}>
                    <Link to={`/members/${m.id}`}
                      className="flex w-full items-center gap-3 px-4 py-3.5 active:bg-slate-50 transition-colors">
                      <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-bold ${avatarColor(m.full_name)}`}>
                        {(m.full_name || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900 truncate flex-1 min-w-0">{m.full_name}</p>
                          <span className="shrink-0 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">Age {m.age}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400 font-mono">{m.roll_no || '—'}</span>
                          <span className="text-slate-200">·</span>
                          <span className="text-xs text-slate-400">{m.date_of_birth || '—'}</span>
                        </div>
                      </div>
                      <svg className="h-4 w-4 text-slate-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6"/>
                      </svg>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* ── Pending Admission Tab ── */}
      {activeTab === 'pending' && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-amber-100 bg-amber-50 px-5 py-3 flex items-center gap-3">
            <span className="text-lg">📋</span>
            <div>
              <p className="text-sm font-bold text-amber-800">Pending Admission List</p>
              <p className="text-xs text-amber-600">Advocates awaiting roll number assignment before becoming members</p>
            </div>
            <Link to="/pending-admissions"
              className="ml-auto shrink-0 rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50">
              Manage / Import →
            </Link>
          </div>

          {pendingLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
          ) : pendingRecords.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-1">
              <p className="text-slate-500 font-medium text-sm">No pending admissions found</p>
              <p className="text-xs text-slate-400">Import a list from the Pending Admissions page</p>
            </div>
          ) : (
            <>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="w-12 px-4 py-3" />
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Petition No</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Gender</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Firm</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                      <th className="px-4 py-3 w-32" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pendingRecords.map((r) => (
                      <tr key={r.id} className="hover:bg-amber-50/40 transition-colors">
                        <td className="px-4 py-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${avatarColor(r.full_name)}`}>
                            {(r.full_name || '?')[0].toUpperCase()}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{r.full_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.petition_no}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{r.gender || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate">{r.firm_name || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{r.phone || '—'}</td>
                        <td className="px-4 py-3">
                          {r.status === 'admitted' ? (
                            <span className="inline-block rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-semibold">
                              Admitted · Roll #{r.roll_no}
                            </span>
                          ) : (
                            <span className="inline-block rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-semibold">Pending</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {r.status !== 'admitted' && (
                            <div className="flex justify-end gap-1.5">
                              {r.member_id && (
                                <button onClick={() => setInviteTarget(r)}
                                  className="rounded-lg border border-orange-200 bg-white hover:bg-orange-50 text-orange-700 px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap">
                                  Invite to Pay
                                </button>
                              )}
                              <button onClick={() => setAdmitTarget(r)}
                                className="rounded-lg bg-orange-600 hover:bg-orange-500 text-white px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap">
                                Admit
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <ul className="sm:hidden divide-y divide-slate-100">
                {pendingRecords.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 px-4 py-3.5">
                    <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-bold ${avatarColor(r.full_name)}`}>
                      {(r.full_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{r.full_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400 font-mono">{r.petition_no}</span>
                        <span className="text-slate-200">·</span>
                        <span className="text-xs text-slate-400">{r.gender || '—'}</span>
                      </div>
                    </div>
                    {r.status !== 'admitted' ? (
                      <div className="flex shrink-0 gap-1.5">
                        {r.member_id && (
                          <button onClick={() => setInviteTarget(r)}
                            className="rounded-lg border border-orange-200 bg-white hover:bg-orange-50 text-orange-700 px-3 py-1.5 text-xs font-medium">
                            Invite
                          </button>
                        )}
                        <button onClick={() => setAdmitTarget(r)}
                          className="rounded-lg bg-orange-600 hover:bg-orange-500 text-white px-3 py-1.5 text-xs font-medium">
                          Admit
                        </button>
                      </div>
                    ) : (
                      <span className="shrink-0 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">#{r.roll_no}</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {!pendingLoading && pendingRecords.length > 0 && (
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
              <p className="text-xs text-slate-400">
                Page {pendingPagination.page} of {pendingPagination.total_pages} · {pendingPagination.total.toLocaleString()} total
              </p>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!pendingPagination.has_previous}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ← Prev
                </button>
                <button onClick={() => setPage(p => p + 1)} disabled={!pendingPagination.has_next}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {admitTarget && (
        <AdmitModal
          record={admitTarget}
          onClose={() => setAdmitTarget(null)}
          onAdmitted={() => { setAdmitTarget(null); loadPending(); }}
        />
      )}

      {inviteTarget && (
        <InviteToPayModal
          record={inviteTarget}
          onClose={() => setInviteTarget(null)}
        />
      )}

      {/* ── Regular Members List (All / Deceased tabs) ── */}
      {activeTab !== 'exempted' && activeTab !== 'pending' && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-1">
              <p className="text-slate-500 font-medium text-sm">No members found</p>
              <p className="text-xs text-slate-400">Try a different search or filter</p>
            </div>
          ) : (
            <>
              {/* ── DESKTOP TABLE ── */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="w-12 px-4 py-3" />
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400 w-28">Roll No</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Chapter</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Phone</th>
                      <th className="px-4 py-3 w-24" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {members.map((m) => (
                      <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${avatarColor(m.full_name)}`}>
                            {(m.full_name || '?')[0].toUpperCase()}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{m.full_name || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{m.roll_no || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusStyle(m.status)}`}>
                              {statusShort(m.status)}
                            </span>
                            {m.membership_type === 'life' && (
                              <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700">Life</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{m.chapter || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 max-w-[160px] truncate">{m.email || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{m.phone || '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/members/${m.id}`}
                            className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors whitespace-nowrap">
                            View →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── MOBILE LIST GROUP ── */}
              <ul className="sm:hidden divide-y divide-slate-100">
                {members.map((m) => (
                  <li key={m.id}>
                    <Link to={`/members/${m.id}`}
                      className="flex w-full items-center gap-3 px-4 py-3.5 active:bg-slate-50 transition-colors">
                      <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-bold ${avatarColor(m.full_name)}`}>
                        {(m.full_name || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900 truncate flex-1 min-w-0">{m.full_name}</p>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusStyle(m.status)}`}>
                            {statusShort(m.status)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400 font-mono">{m.roll_no || '—'}</span>
                          {m.phone && <>
                            <span className="text-slate-200">·</span>
                            <span className="text-xs text-slate-400 truncate">{m.phone}</span>
                          </>}
                        </div>
                      </div>
                      <svg className="h-4 w-4 text-slate-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6"/>
                      </svg>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Pagination footer */}
          {!loading && members.length > 0 && (
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
              <p className="text-xs text-slate-400">
                Page {pagination.page} of {pagination.total_pages} · {pagination.total.toLocaleString()} total
              </p>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!pagination.has_previous}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ← Prev
                </button>
                <button onClick={() => setPage(p => p + 1)} disabled={!pagination.has_next}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
